import { useEffect, useRef, useState, useCallback, useLayoutEffect, memo, useMemo } from 'react'
import { Card, Typography, Empty, Space, Tag, Button, Badge, Grid, Skeleton } from 'antd'
import { RobotOutlined, ClockCircleOutlined, SyncOutlined, TrophyOutlined } from '@ant-design/icons'
import { marked } from 'marked'
import { getMessageTypeIcon, getMessageTypeLabel, formatTime } from '../../utils/helpers'
import './ProcessingIndicator.css'

const { Title, Text } = Typography

marked.setOptions({
  gfm: true,
  breaks: true,  // 允许单个换行符也产生换行效果
})

// Memoized markdown renderer component
const MarkdownContent = memo(({ content }) => {
  const html = useMemo(() => marked.parse(content || ''), [content])
  return <div className="markdown-content" dangerouslySetInnerHTML={{ __html: html }} />
})

const MessagePanel = ({
  messages,
  isProcessing = false,
  taskStatus,
  lastUpdateTime,
  awaitingPayment = false,
  onViewReports,
  isLoading = false,
}) => {
  const containerRef = useRef(null)
  const screens = Grid.useBreakpoint()
  const isMobile = !screens.md
  const [newMessageIds, setNewMessageIds] = useState(new Set())
  const [expandedIds, setExpandedIds] = useState(new Set())
  const [overflowingIds, setOverflowingIds] = useState(new Set())
  const contentRefs = useRef({})
  const prevMessageCountRef = useRef(0)

  const COLLAPSED_HEIGHT = isMobile ? 200 : 260
  const LONG_CONTENT_THRESHOLD = isMobile ? 360 : 480

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = 0
    }

    // Detect new messages and add animation
    if (messages.length > prevMessageCountRef.current) {
      const newIds = new Set()
      messages.slice(0, messages.length - prevMessageCountRef.current).forEach((msg) => {
        newIds.add(msg.id || `${msg.createdAt}-${msg.messageType}`)
      })
      setNewMessageIds(newIds)

      // Remove animation class after animation completes
      setTimeout(() => {
        setNewMessageIds(new Set())
      }, 2000)
    }

    prevMessageCountRef.current = messages.length
  }, [messages])

  useEffect(() => {
    const validIds = new Set(messages.map((msg) => msg.id || `${msg.createdAt}-${msg.messageType}`))

    setExpandedIds((prev) => {
      const next = new Set()
      prev.forEach((id) => {
        if (validIds.has(id)) next.add(id)
      })
      return next
    })

    Object.keys(contentRefs.current).forEach((key) => {
      if (!validIds.has(key)) {
        delete contentRefs.current[key]
      }
    })
  }, [messages])

  const measureOverflow = useCallback(() => {
    const nextOverflow = new Set()
    Object.entries(contentRefs.current).forEach(([id, element]) => {
      if (!element) return
      const contentHeight = element.scrollHeight
      // 确保内容真正超出折叠高度才显示展开按钮
      if (contentHeight > COLLAPSED_HEIGHT + 20) {
        nextOverflow.add(id)
      }
    })

    setOverflowingIds((prev) => {
      if (prev.size === nextOverflow.size) {
        let allMatch = true
        prev.forEach((value) => {
          if (!nextOverflow.has(value)) {
            allMatch = false
          }
        })
        if (allMatch) {
          nextOverflow.forEach((value) => {
            if (!prev.has(value)) {
              allMatch = false
            }
          })
        }
        if (allMatch) {
          return prev
        }
      }
      return nextOverflow
    })
  }, [COLLAPSED_HEIGHT])

  useLayoutEffect(() => {
    measureOverflow()
  }, [messages, measureOverflow])

  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') {
      return undefined
    }

    const observer = new ResizeObserver(() => {
      measureOverflow()
    })

    Object.values(contentRefs.current).forEach((element) => {
      if (element) {
        observer.observe(element)
      }
    })

    return () => observer.disconnect()
  }, [messages, measureOverflow])

  const toggleExpand = (messageId) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(messageId)) {
        next.delete(messageId)
      } else {
        next.add(messageId)
      }
      return next
    })

    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => measureOverflow())
    } else {
      setTimeout(() => measureOverflow(), 0)
    }
  }

  const buildMessageContent = (message) => {
    const { content } = message

    let stringContent = ''
    let jsonCandidate = null

    if (typeof content === 'object' && content !== null) {
      if (typeof content.content === 'string') {
        stringContent = normalizeMultilineContent(content.content)
      } else if (typeof content.text === 'string') {
        stringContent = normalizeMultilineContent(content.text)
      } else if (content.content && typeof content.content === 'object') {
        jsonCandidate = content.content
        stringContent = normalizeMultilineContent(JSON.stringify(content.content, null, 2))
      } else {
        jsonCandidate = content
        stringContent = normalizeMultilineContent(JSON.stringify(content, null, 2))
      }
    } else if (typeof content === 'string') {
      stringContent = normalizeMultilineContent(content)
    } else {
      stringContent = normalizeMultilineContent(String(content || ''))
    }

    if (!jsonCandidate) {
      jsonCandidate = tryParseJsonString(stringContent)
    }

    const lengthHint = stringContent.length

    if (jsonCandidate) {
      return {
        node: renderJsonContent(jsonCandidate),
        lengthHint: JSON.stringify(jsonCandidate).length || lengthHint,
      }
    }

    // CSV/键值表格检测暂时关闭，避免 Markdown 内容被误识别
    // const tableSection = extractTabularSection(stringContent)
    // if (tableSection) {
    //   return {
    //     node: renderCsvSection(tableSection),
    //     lengthHint,
    //   }
    // }

    return {
      node: <MarkdownContent content={stringContent || ''} />,
      lengthHint,
    }
  }

  const renderJsonContent = (data) => {
    if (Array.isArray(data) && data.length > 0 && data.every((item) => item && typeof item === 'object' && !Array.isArray(item))) {
      const headers = Array.from(new Set(data.flatMap((item) => Object.keys(item))))
      const rows = data.map((item) => headers.map((key) => formatTableCell(item[key])))
      return renderTable(headers, rows)
    }

    return (
      <pre className="json-content">
        {JSON.stringify(data, null, 2)}
      </pre>
    )
  }

  const renderCsvSection = ({ headers, rows, before, after }) => (
    <div>
      {before && before.trim() && <MarkdownContent content={before} />}
      {renderTable(headers, rows)}
      {after && after.trim() && <MarkdownContent content={after} />}
    </div>
  )

  const renderTable = (headers, rows) => (
    <div className="csv-table-wrapper">
      <table className="csv-table">
        <thead>
          <tr>
            {headers.map((header, index) => (
              <th key={`${header}-${index}`}>{header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {row.map((cell, cellIndex) => (
                <td key={`${rowIndex}-${cellIndex}`}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )

  const tryParseJsonString = (value) => {
    if (typeof value !== 'string') return null
    const trimmed = value.trim()
    if (!trimmed) return null
    if (!['{', '['].includes(trimmed[0])) return null

    try {
      const parsed = JSON.parse(trimmed)
      return typeof parsed === 'object' && parsed !== null ? parsed : null
    } catch (error) {
      return null
    }
  }

  const extractTabularSection = (text) => {
    if (!text || typeof text !== 'string') return null

    const lines = text.split('\n')
    let currentBlock = []
    let startIndex = -1

    const flushBlock = (endIndex) => {
      if (currentBlock.length === 0) return null

      const blockLines = currentBlock.slice()
      currentBlock = []

      const hasComma = blockLines.every(isCommaSeparatedLine)
      const hasColon = !hasComma && blockLines.every(isColonSeparatedLine)

      if (!hasComma && !hasColon) return null
      if (hasColon && blockLines.length < 3) return null

      const { headers, rows } = hasComma
        ? parseCommaSeparatedBlock(blockLines)
        : parseColonSeparatedBlock(blockLines)

      if (!rows.length) return null

      const before = lines.slice(0, startIndex).join('\n').trim()
      const after = lines.slice(endIndex).join('\n').trim()

      return { headers, rows, before, after }
    }

    for (let index = 0; index <= lines.length; index += 1) {
      const line = lines[index] ?? ''
      const trimmed = line.trim()

      if (isPotentialTableLine(trimmed)) {
        if (currentBlock.length === 0) {
          startIndex = index
        }
        currentBlock.push(trimmed)
      } else if (currentBlock.length > 0) {
        const blockResult = flushBlock(index)
        if (blockResult) {
          return blockResult
        }
        startIndex = -1
      }
    }

    return null
  }

  const isPotentialTableLine = (line) => {
    if (!line) return false
    if (/^#{1,6}\s/.test(line)) return false
    if (/^\s*[-*+]\s/.test(line)) return false
    if (line.startsWith('```')) return false
    return isCommaSeparatedLine(line) || isColonSeparatedLine(line)
  }

  const isCommaSeparatedLine = (line) => {
    if (!line.includes(',')) return false
    const cells = line.split(',').map((cell) => cell.trim())
    return cells.filter(Boolean).length >= 2
  }

  const isColonSeparatedLine = (line) => {
    if (!line.includes(':')) return false
    if (/^https?:\/\//i.test(line)) return false

    // 排除 Markdown 列表 (有序/无序)
    if (/^\s*\d+\.\s/.test(line)) return false  // 1. 2. 3.
    if (/^\s*[-*+]\s/.test(line)) return false  // - * +

    // 排除包含 Markdown 加粗语法的行 (通常是列表项)
    if (/\*\*[^*]+\*\*/.test(line)) return false

    const [first, ...rest] = line.split(':')
    const key = first.trim()
    const value = rest.join(':').trim()

    if (!key || !value) return false

    // Key 应该相对简短 (真实的 key-value 表格)
    if (key.length > 50) return false

    // Key 不应该包含句子结束标点
    if (/[.!?]/.test(key)) return false

    // Key 应该比较简洁，不应该有太多空格
    const keyWords = key.split(/\s+/)
    if (keyWords.length > 6) return false

    // Value 应该相对简短 (避免误判长句子)
    if (value.length > 200) return false

    return true
  }

  const parseCommaSeparatedBlock = (blockLines) => {
    const rows = blockLines.map((line) => line.split(',').map((cell) => cell.trim()))
    const columnCount = Math.max(...rows.map((row) => row.length))

    const alignedRows = rows.map((row) => {
      if (row.length === columnCount) return row
      return [...row, ...new Array(columnCount - row.length).fill('')]
    })

    const headerCandidate = alignedRows[0]
    const headerIsTextual = headerCandidate.some((cell) => /[A-Za-z]/.test(cell))

    const headers = headerIsTextual
      ? headerCandidate.map((cell, index) => formatTableCell(cell) || `Column ${index + 1}`)
      : headerCandidate.map((_, index) => `Column ${index + 1}`)

    const dataRows = headerIsTextual ? alignedRows.slice(1) : alignedRows

    return {
      headers,
      rows: dataRows.map((row) => row.map((cell) => formatTableCell(cell))),
    }
  }

  const parseColonSeparatedBlock = (blockLines) => {
    const rows = blockLines
      .map((line) => {
        const [first, ...rest] = line.split(':')
        if (!rest.length) return null
        const key = first.trim()
        const value = rest.join(':').trim()
        return [formatTableCell(key), formatTableCell(value)]
      })
      .filter(Boolean)

    return {
      headers: ['Label', 'Value'],
      rows,
    }
  }

  const formatTableCell = (value) => {
    if (value === null || value === undefined) return ''
    if (typeof value === 'number' && Number.isFinite(value)) return value.toString()
    if (typeof value === 'string') return value
    return JSON.stringify(value)
  }

  const getMessageBorderColor = (type) => {
    const colors = {
      System: '#4299e1',
      Reasoning: '#48bb78',
      tool_call: '#ed8936',
      Analysis: '#9f7aea',
    }
    return colors[type] || '#cbd5e0'
  }

  const getMessageBackground = (type) => {
    const backgrounds = {
      System: '#ebf8ff',
      Reasoning: '#f0fff4',
      tool_call: '#fffaf0',
      Analysis: '#faf5ff',
    }
    return backgrounds[type] || '#f7fafc'
  }

  const normalizeMultilineContent = (value) => {
    if (typeof value !== 'string') return value

    let normalized = value
      .replace(/\r\n/g, '\n')
      .replace(/\\r\\n/g, '\n')
      .replace(/\\n/g, '\n')
      .replace(/\\t/g, '\t')

    const trimmed = normalized.trim()
    if ((trimmed.startsWith('"') && trimmed.endsWith('"')) || (trimmed.startsWith("'") && trimmed.endsWith("'"))) {
      normalized = trimmed.slice(1, -1)
    }

    return normalized
      .replace(/\\"/g, '"')
      .replace(/\\'/g, "'")
      .replace(/\\\\/g, '\\')
      .replace(/\\u([\dA-Fa-f]{4})/g, (_, hex) => String.fromCharCode(parseInt(hex, 16)))
  }

  const getProcessingInfo = () => {
    if (taskStatus === 'RUNNING') {
      return {
        icon: <RobotOutlined className="spinning" style={{ fontSize: isMobile ? 14 : 16 }} />,
        text: 'AI Analysis in Progress',
        badge: 'LIVE',
        color: '#667eea',
        showDots: true,
      }
    }
    if (taskStatus === 'PENDING') {
      if (awaitingPayment) {
        return null
      }
      return {
        icon: <ClockCircleOutlined className="pulse" style={{ fontSize: isMobile ? 14 : 16 }} />,
        text: 'Queued for Processing',
        badge: 'PENDING',
        color: '#f59e0b',
        showDots: false,
      }
    }
    return null
  }

  const timeSinceUpdate = lastUpdateTime
    ? Math.floor((Date.now() - new Date(lastUpdateTime).getTime()) / 1000)
    : 0

  const processingInfo = getProcessingInfo()
  const isCompleted = taskStatus === 'COMPLETED'

  const renderHeader = () => {
    const statusBlock = processingInfo ? (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexWrap: 'wrap',
        }}
      >
        {processingInfo.badge && (
          <Badge
            count={processingInfo.badge}
            style={{
              background: processingInfo.color,
              color: '#fff',
              fontSize: isMobile ? 9 : 10,
              fontWeight: 700,
              height: isMobile ? 16 : 18,
              lineHeight: `${isMobile ? 16 : 18}px`,
              borderRadius: 9,
              padding: `0 ${isMobile ? 6 : 8}px`,
            }}
          />
        )}
        <span style={{ color: processingInfo.color }}>{processingInfo.icon}</span>
        <Text strong style={{ color: '#1f2933', fontSize: isMobile ? 12 : 13 }}>
          {processingInfo.text}
        </Text>
        {processingInfo.showDots && (
          <div className="typing-dots" style={{ marginLeft: 2 }}>
            <span></span>
            <span></span>
            <span></span>
          </div>
        )}
      </div>
    ) : null

    const messageCountTag = messages.length > 0 ? (
      <Tag
        color="blue"
        style={{
          fontSize: isMobile ? 11 : 12,
          padding: `2px ${isMobile ? 8 : 10}px`,
        }}
      >
        {messages.length} messages
      </Tag>
    ) : null

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center',
          justifyContent: 'space-between',
          gap: isMobile ? 12 : 16,
          width: '100%',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 8 : 6, flex: '1 1 auto' }}>
          <div
            style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: isMobile ? 'flex-start' : 'center',
              justifyContent: isMobile ? 'flex-start' : 'space-between',
              gap: 8,
              flexWrap: 'wrap',
              width: '100%',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flexWrap: 'wrap',
              }}
            >
              <Title level={5} style={{ margin: 0, fontSize: isMobile ? 16 : undefined }}>
                Messages & Tools
              </Title>
              {messageCountTag}
            </div>
            {!isMobile && statusBlock}
          </div>
          {isMobile && statusBlock}
        </div>

        {!isProcessing && isCompleted && onViewReports && (
          <Button
            onClick={onViewReports}
            type="primary"
            size="middle"
            className="view-reports-button premium-btn"
            block={isMobile}
            style={{
              fontWeight: 600,
              borderRadius: 10,
              fontSize: '13px',
              height: isMobile ? '40px' : '36px',
              paddingLeft: isMobile ? '16px' : '18px',
              paddingRight: isMobile ? '16px' : '18px',
              border: 'none',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              boxShadow: '0 3px 12px rgba(102, 126, 234, 0.2), 0 0 0 1px rgba(102, 126, 234, 0.1)',
              position: 'relative',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
              📊 View Reports
            </span>
          </Button>
        )}
      </div>
    )
  }

  const isEmptyState = !isLoading && messages.length === 0
  const shouldCenterState = isEmptyState || isLoading

  const contentContainerStyle = {
    maxHeight: '1000px',
    overflowY: shouldCenterState ? 'hidden' : 'auto',
    padding: isMobile ? 12 : 16,
    display: shouldCenterState ? 'flex' : 'block',
    alignItems: shouldCenterState ? 'center' : undefined,
    justifyContent: shouldCenterState ? 'center' : undefined,
    minHeight: shouldCenterState ? (isMobile ? 260 : 460) : undefined,
    width: '100%',
  }

  return (
    <Card
      className="message-panel"
      title={renderHeader()}
      style={{ display: 'flex', flexDirection: 'column', flex: 1, height: '100%' }}
      bodyStyle={{ flex: 1, overflow: 'hidden', padding: 0 }}
    >
      <div
        ref={containerRef}
        style={contentContainerStyle}
      >
        {isLoading ? (
          <div className="message-panel__loading-state">
            <Skeleton active paragraph={{ rows: isMobile ? 3 : 4 }} style={{ width: '100%' }} />
            <Skeleton
              active
              paragraph={{ rows: isMobile ? 3 : 4 }}
              style={{ width: '100%' }}
            />
          </div>
        ) : isEmptyState ? (
          <Empty description="No messages yet" style={{ marginTop: 0 }} />
        ) : (
          <Space direction="vertical" size={isMobile ? 10 : 12} style={{ width: '100%' }}>
            {messages.map((message) => {
              const messageId = message.id || `${message.createdAt}-${message.messageType}`
              const isNewMessage = newMessageIds.has(messageId)
              const isExpanded = expandedIds.has(messageId)
              const isOverflowing = overflowingIds.has(messageId)
              const { node: contentNode, lengthHint } = buildMessageContent(message)
              // 只有当内容真正超出折叠高度时才显示展开按钮
              const contentHeight = contentRefs.current[messageId]?.scrollHeight || 0
              const hasRealOverflow = contentHeight > COLLAPSED_HEIGHT + 10
              const hasLongContent = hasRealOverflow || lengthHint > LONG_CONTENT_THRESHOLD

              // 确保只有在真正有更多内容可显示时才显示展开按钮
              const shouldShowToggle = hasLongContent && !isExpanded
              const showToggle = shouldShowToggle || isExpanded
              const showGradient = !isExpanded && hasLongContent

              return (
                <div
                  key={messageId}
                  className={`slide-in ${isNewMessage ? 'message-slide-in new-message-highlight' : ''}`}
                  style={{
                    padding: isMobile ? 12 : 15,
                    borderLeft: `4px solid ${getMessageBorderColor(message.messageType)}`,
                    background: getMessageBackground(message.messageType),
                    borderRadius: 6,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: 10,
                    }}
                  >
                    <Text strong style={{ fontSize: isMobile ? 12 : 13 }}>
                      {getMessageTypeIcon(message.messageType)} {getMessageTypeLabel(message.messageType)}
                    </Text>
                    <Text type="secondary" style={{ fontSize: isMobile ? 10 : 11 }}>
                      {formatTime(message.createdAt)}
                    </Text>
                  </div>
                  <div
                    className={`message-content-wrapper ${isExpanded ? 'expanded' : 'collapsed'}`}
                  >
                    <div
                      className="message-content-body"
                      style={{
                        maxHeight: isExpanded ? 'none' : COLLAPSED_HEIGHT,
                        transition: 'max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      }}
                    >
                      <div
                        ref={(element) => {
                          if (element) {
                            contentRefs.current[messageId] = element
                          } else {
                            delete contentRefs.current[messageId]
                          }
                        }}
                        className="message-content-inner"
                      >
                        <div style={{ fontSize: isMobile ? 12 : 13, color: '#4a5568', lineHeight: 1.7 }}>
                          {contentNode}
                        </div>
                      </div>
                    </div>
                    {showGradient && <div className="message-content-gradient" />}
                  </div>
                  {showToggle && (
                    <Button
                      type="link"
                      size="small"
                      className="expand-toggle-btn"
                      style={{
                        padding: '4px 8px',
                        marginTop: 8,
                        fontSize: isMobile ? '11px' : '12px',
                        fontWeight: 500,
                        color: '#667eea',
                        border: '1px solid #e0e7ff',
                        borderRadius: 4,
                        transition: 'all 0.2s ease'
                      }}
                      onClick={() => toggleExpand(messageId)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#f0f3ff';
                        e.currentTarget.style.borderColor = '#667eea';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.borderColor = '#e0e7ff';
                      }}
                    >
                      <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        {isExpanded ? (
                          <>
                            ↑ <span>Show less</span>
                          </>
                        ) : (
                          <>
                            ↓ <span>Show more</span>
                          </>
                        )}
                      </span>
                    </Button>
                  )}
                </div>
              )
            })}
          </Space>
        )}
      </div>
    </Card>
  )
}

export default memo(MessagePanel)
